import { useMemo, useState } from 'react';
import { GitCommit, Building2, UserRound, Lock, Beaker, Puzzle, Ban } from 'lucide-react';
import {
  useGetClaimExpenditure,
  useGetClaimProjectBreakdown,
  useGetRdShares,
  getGetRdSharesQueryKey,
  type ProjectRdShare,
  type GetClaimProjectBreakdown200DataProjectsItem,
  type GetClaimExpenditure200DataLabourByUserItem,
} from '@edison/api-client-react';
import { Card } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { SkeletonCard, SkeletonMetricTile } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { EligibilityChip, type EligibilityStatus } from '@/pages/expenditure/eligibility-chip.js';
import { CommitDrilldownPanel } from './commit-drilldown-panel.js';
import { categoryLabel } from '@/lib/non-labour-category.js';
import { ClaimActivitiesSection } from './claim-activities-section.js';
import { ClaimProjectGroupsSection } from './claim-project-groups-section.js';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

function pctStr(v: string | null | undefined): number {
  return v != null ? parseFloat(v) : 0;
}

function formatPct(n: number): string {
  return `${Number.isInteger(n) ? n : Math.round(n * 10) / 10}%`;
}

// R&DTI refundable offset: 43.5% for entities with turnover < $20M — the same legislated constant
// already used on claim-detail.tsx / claim-list.tsx / claim-schedule1-tab.tsx (never re-derived).
const REFUNDABLE_OFFSET_RATE = 0.435;

const ACTIVITY_TYPE_META: Record<string, { label: string; variant: 'positive' | 'info' | 'default'; Icon: typeof Beaker }> = {
  core: { label: 'Core', variant: 'positive', Icon: Beaker },
  supporting: { label: 'Supporting', variant: 'info', Icon: Puzzle },
  non_rd: { label: 'Not R&D', variant: 'default', Icon: Ban },
};

interface ApiErrorShape {
  response?: { data?: { error?: { code?: string; message?: string; details?: unknown } } };
}

function extractAwaitingProjects(err: unknown): string[] | null {
  const shape = err as ApiErrorShape | null;
  if (shape?.response?.data?.error?.code !== 'SHARES_NOT_ADOPTED') return null;
  const details = shape.response.data.error.details;
  return Array.isArray(details) ? (details as string[]) : [];
}

interface NonLabourItem {
  id: string;
  description?: string | null;
  category: string;
  amount: string;
  eligibilityStatus?: string;
  claimablePercent?: string;
}

function BasisChip({ row }: { row: GetClaimExpenditure200DataLabourByUserItem }) {
  if (row.employmentType === 'casual') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap text-muted-foreground"
        title="Priced on capped R&D hours × hourly rate — no salary or employment-fraction term."
      >
        <UserRound className="h-3 w-3 shrink-0" aria-hidden="true" />
        Casual · hours × rate
      </span>
    );
  }
  if (row.employmentType === 'salaried') {
    const fracPct = row.employmentFrac != null ? Math.round(row.employmentFrac * 100) : null;
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap text-muted-foreground"
        title="Priced on loaded salary (base + super + payroll tax + insurances) × employment fraction × session-weighted R&D share."
      >
        <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
        Salaried{fracPct != null ? ` · ${fracPct}% FY` : ''}
      </span>
    );
  }
  return null;
}

interface Props {
  claimId: string;
  incomeYear: string;
}

/**
 * Activity Map tab (Stage 1 apportionment evidence store, Task 17): replicates the information
 * architecture of the 18 Sep working-paper artefact
 * (docs/superpowers/artifacts/2026-09-18-activity-map-live-claim-79d46851.html) natively inside
 * Edison, driven entirely by real API data — claim header roll-up, per-project activity cards
 * (Edison ties one activity to exactly one project, unlike the artefact's cross-project activity
 * themes — this view groups activity cards BY PROJECT, which is the faithful adaptation of the
 * artefact's "project feeds activity" relationship to Edison's actual schema), a per-person table,
 * and a non-labour cost table. Apportionment mode only — every other mode shows a short
 * explanation instead of fabricating a view over a methodology this page was never designed for.
 */
export function ActivityMapTab({ claimId, incomeYear }: Props) {
  const expenditureQuery = useGetClaimExpenditure(claimId);
  const breakdownQuery = useGetClaimProjectBreakdown(claimId);
  const sharesQuery = useGetRdShares({ incomeYear }, { query: { queryKey: getGetRdSharesQueryKey({ incomeYear }) } });

  const [drilldown, setDrilldown] = useState<{ projectId: string; projectName: string } | null>(null);

  const shareByProjectId = useMemo(() => {
    const map = new Map<string, ProjectRdShare>();
    for (const s of sharesQuery.data?.data?.items ?? []) map.set(s.projectId, s);
    return map;
  }, [sharesQuery.data]);

  if (breakdownQuery.isLoading || sharesQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonMetricTile key={i} />)}
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (breakdownQuery.error) {
    return <InlineError message="Failed to load the Activity Map." onRetry={() => void breakdownQuery.refetch()} />;
  }

  const breakdown = breakdownQuery.data?.data;
  const mode = breakdown?.mode ?? expenditureQuery.data?.data?.mode;

  if (mode !== 'apportionment') {
    return (
      <Card variant="static" className="p-8 text-center space-y-2">
        <p className="text-sm font-medium">Activity Map is only available under apportionment mode.</p>
        <p className="text-sm text-muted-foreground">
          This claim is using a different R&amp;D-value methodology. Switch to apportionment mode on the
          Expenditure tab to see the activity map view.
        </p>
      </Card>
    );
  }

  const isLocked = breakdown?.basis === 'snapshot';
  const projects: GetClaimProjectBreakdown200DataProjectsItem[] = [...(breakdown?.projects ?? [])]
    .sort((a, b) => (b.totalRdExpenditure ?? 0) - (a.totalRdExpenditure ?? 0));

  const awaitingProjects = extractAwaitingProjects(expenditureQuery.error) ?? breakdown?.apportionmentAwaitingProjects ?? [];
  const projectNameById = new Map(projects.map((p) => [p.id, p.name]));

  const labourTotal = expenditureQuery.data?.data?.labour?.totalRdExpenditure ?? breakdown?.totalRdExpenditure ?? 0;
  const nonLabourTotal = expenditureQuery.data?.data?.nonLabour?.totalNonLabourExpenditure ?? 0;
  const grandTotal = expenditureQuery.data?.data?.totalExpenditure ?? (labourTotal + nonLabourTotal);
  const offset = grandTotal * REFUNDABLE_OFFSET_RATE;

  let coreTotal = 0;
  let supportingTotal = 0;
  // Unallocated labour DOLLARS (engine 'null' bucket — e.g. an adopted supporting share with no
  // supporting activity to receive it). Shown as a third header figure whenever non-zero so
  // core + supporting + unallocated always reconciles to the labour total — a claim-evidence view
  // must never show a split that silently under-sums its own total (Task 17 review fix).
  let unallocatedTotal = 0;
  for (const p of projects) {
    for (const a of p.activities ?? []) {
      if (a.activityType === 'core') coreTotal += a.rdExpenditure ?? 0;
      else if (a.activityType === 'supporting') supportingTotal += a.rdExpenditure ?? 0;
    }
    unallocatedTotal += p.untaggedRdExpenditure ?? 0;
  }

  const byUser = expenditureQuery.data?.data?.labour?.byUser ?? [];
  const nonLabourItems = (expenditureQuery.data?.data?.nonLabour?.items ?? []) as unknown as NonLabourItem[];
  const nonLabourByCategory = expenditureQuery.data?.data?.nonLabour?.byCategory ?? [];
  const nonLabourClaimablePercent = expenditureQuery.data?.data?.nonLabour?.claimablePercent ?? null;

  const noActivitiesAtAll = projects.length === 0 || projects.every((p) => (p.activities ?? []).length === 0);

  return (
    <div className="space-y-6">
      <ClaimActivitiesSection claimId={claimId} />
      <ClaimProjectGroupsSection claimId={claimId} />
      {/* ── Header roll-up ── */}
      <Card variant="static" className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-medium">Activity Map — {incomeYear}</h2>
          {isLocked && (
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" aria-hidden="true" />
              Locked · frozen at lock time
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Total R&amp;D claim</p>
            <p className="text-2xl font-semibold font-mono tabular-nums">{formatCurrency(grandTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Labour / Non-labour</p>
            <p className="text-sm font-medium font-mono tabular-nums">
              {formatCurrency(labourTotal)} / {nonLabourTotal > 0 ? formatCurrency(nonLabourTotal) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              Labour core / supporting{unallocatedTotal > 0 ? ' / unallocated' : ''}
            </p>
            <p className="text-sm font-medium font-mono tabular-nums">
              {coreTotal > 0 || supportingTotal > 0 || unallocatedTotal > 0
                ? `${formatCurrency(coreTotal)} / ${formatCurrency(supportingTotal)}${unallocatedTotal > 0 ? ` / ${formatCurrency(unallocatedTotal)}` : ''}`
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Est. refundable offset (43.5%)</p>
            <p className="text-sm font-medium font-mono tabular-nums">{grandTotal > 0 ? formatCurrency(offset) : '—'}</p>
          </div>
        </div>

        {awaitingProjects.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
            R&amp;D shares not yet adopted for: {awaitingProjects.map((id) => projectNameById.get(id) ?? id.slice(0, 8)).join(', ')}.{' '}
            Adopt each project&apos;s share in the R&amp;D Shares tab before its labour figure can compute.
          </div>
        )}
      </Card>

      {/* ── Project → activity cards ── */}
      {noActivitiesAtAll ? (
        <Card variant="static" className="p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No R&amp;D activities captured yet for {incomeYear}. Set up a project with a locked TFA and add
            its activities to see the activity map.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {projects.filter((p) => (p.activities ?? []).length > 0 || (p.untaggedCappedRdHours ?? 0) > 0 || (p.untaggedRdExpenditure ?? 0) > 0).map((project) => {
            const share = shareByProjectId.get(project.id ?? '');
            const adopted = share?.status === 'adopted';
            return (
              <Card key={project.id} variant="static" className="p-5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold">{project.name}</h3>
                    {adopted ? (
                      <Badge variant="default">
                        Adopted: {formatPct(pctStr(share!.adoptedCorePct))} core · {formatPct(pctStr(share!.adoptedSupportingPct))} supporting
                      </Badge>
                    ) : (
                      <Badge variant="warning">Share not adopted</Badge>
                    )}
                    {project.tfa?.determination && (
                      <span className="text-xs text-muted-foreground">TFA: {project.tfa.determination}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold tabular-nums">{formatCurrency(project.totalRdExpenditure ?? 0)}</span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      onClick={() => setDrilldown({ projectId: project.id ?? '', projectName: project.name ?? '' })}
                    >
                      <GitCommit className="h-3.5 w-3.5" aria-hidden="true" />
                      Commit evidence
                    </button>
                  </div>
                </div>

                <div className="divide-y">
                  {(project.activities ?? []).map((activity) => {
                    const meta = ACTIVITY_TYPE_META[activity.activityType ?? 'core'] ?? ACTIVITY_TYPE_META['core']!;
                    const Icon = meta.Icon;
                    const ofClaim = labourTotal > 0 ? ((activity.rdExpenditure ?? 0) / labourTotal) * 100 : 0;
                    return (
                      <div key={activity.id} className="py-2.5 first:pt-0 last:pb-0 space-y-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="flex items-center gap-2 min-w-0">
                            <Badge variant={meta.variant}>
                              <Icon className="h-3 w-3 mr-1" aria-hidden="true" />
                              {meta.label}
                            </Badge>
                            <span className="text-sm font-medium truncate">{activity.name}</span>
                          </span>
                          <span className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-muted-foreground tabular-nums">{formatPct(ofClaim)} of labour</span>
                            <span className="font-mono text-sm tabular-nums">{formatCurrency(activity.rdExpenditure ?? 0)}</span>
                          </span>
                        </div>
                        {activity.description && (
                          <p className="text-xs text-muted-foreground pl-1">{activity.description}</p>
                        )}
                      </div>
                    );
                  })}
                  {((project.untaggedCappedRdHours ?? 0) > 0 || (project.untaggedRdExpenditure ?? 0) > 0) && (
                    <div className="py-2.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>Unallocated to activity</span>
                      <span className="flex items-center gap-3 font-mono tabular-nums">
                        <span>{(project.untaggedCappedRdHours ?? 0).toFixed(1)}h</span>
                        {(project.untaggedRdExpenditure ?? 0) > 0 && (
                          <span>{formatCurrency(project.untaggedRdExpenditure ?? 0)}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Per-person table ── */}
      <Card variant="static" className="p-5 space-y-3">
        <h3 className="text-sm font-medium">By person</h3>
        {byUser.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No approved R&amp;D labour captured yet{awaitingProjects.length > 0 ? ' — see the shares awaiting adoption above' : ''}.
          </p>
        ) : (
          <div className="divide-y">
            {byUser.map((row) => (
              <div key={row.userId} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-sm truncate">{row.name ?? row.email ?? row.userId}</span>
                  <BasisChip row={row} />
                </span>
                <span className="flex items-center gap-3 shrink-0">
                  {row.rdPercent != null && (
                    <span className="text-xs text-muted-foreground tabular-nums">{formatPct(row.rdPercent)} R&amp;D</span>
                  )}
                  <span className="font-mono text-sm font-medium tabular-nums">{formatCurrency(row.rdExpenditure ?? 0)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Non-labour costs — category table (the number and the rate), line items beneath ── */}
      <Card variant="static" className="p-5 space-y-3">
        <h3 className="text-sm font-medium">Non-labour costs</h3>
        {nonLabourByCategory.length > 0 && (
          <div className="space-y-2">
            {nonLabourClaimablePercent != null && (
              <p className="text-xs text-muted-foreground">Claimable at {nonLabourClaimablePercent}% of captured spend.</p>
            )}
            <table className="w-full text-sm" aria-label="Non-labour by category">
              <thead className="text-xs text-muted-foreground">
                <tr><th className="text-left font-medium py-1">Category</th><th className="text-right font-medium py-1">Spend</th><th className="text-right font-medium py-1">Claimable</th></tr>
              </thead>
              <tbody>
                {nonLabourByCategory.map((row) => (
                  <tr key={row.category} className="border-t">
                    <td className="py-1.5">{categoryLabel(row.category)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums">{formatCurrency(row.amount)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums">{formatCurrency(row.claimableAmount)}</td>
                  </tr>
                ))}
                <tr className="border-t font-medium">
                  <td className="py-1.5">Total</td>
                  <td className="py-1.5 text-right font-mono tabular-nums">{formatCurrency(nonLabourByCategory.reduce((s, r) => s + r.amount, 0))}</td>
                  <td className="py-1.5 text-right font-mono tabular-nums">{formatCurrency(nonLabourTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {nonLabourItems.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">—</p>
        ) : (
          <details className="divide-y">
            <summary className="cursor-pointer text-xs text-muted-foreground py-1">Line items ({nonLabourItems.length})</summary>
            {nonLabourItems.map((item) => {
              const status = (item.eligibilityStatus ?? 'claimable') as EligibilityStatus;
              return (
                <div key={item.id} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-muted-foreground truncate">{item.description ?? item.category}</span>
                    <EligibilityChip status={status} percent={status === 'claimable' ? item.claimablePercent : undefined} className="shrink-0" />
                  </span>
                  <span className="font-mono tabular-nums shrink-0">{formatCurrency(parseFloat(item.amount))}</span>
                </div>
              );
            })}
            <div className="flex items-center justify-between text-sm font-medium pt-2">
              <span>Non-labour total (claimable)</span>
              <span className="font-mono tabular-nums">{formatCurrency(nonLabourTotal)}</span>
            </div>
          </details>
        )}
      </Card>

      <CommitDrilldownPanel
        open={drilldown !== null}
        onClose={() => setDrilldown(null)}
        projectId={drilldown?.projectId ?? null}
        projectName={drilldown?.projectName ?? ''}
        incomeYear={incomeYear}
      />
    </div>
  );
}
